import { Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import DeclarationCohabitation from 'mutation/components/declaration-cohabitation';
import ModalAlreadyInCohabitation from 'mutation/components/declaration-cohabitation/already-in-cohabitation-modal';
import ExitModal from 'mutation/components/shared/exit-modal';
import { DeclarationCohabitationCrumbs } from 'mutation/constants/declaration-cohabitation-crumbs';
import { validationSchema } from 'mutation/constants/declaration-cohabitation-validation';
import { transformerDeclarationCohabitationData } from 'mutation/utils/transformer-declaration-cohabitation';
import { transformerRequestDeclarationCohabitationData } from 'mutation/utils/transformer-request-declaration-cohabitation';
import { DeclarationCohabitationFormVM } from 'mutation/view-models/declaration-cohabitation';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { PmiButton } from 'shell/components/shared/button';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { StepButton, StepTypography } from 'shell/components/shared/step-button';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import PmiTitle from 'shell/components/shared/title';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { ItemVM } from 'shell/view-models/item';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';

const DeclarationCohabitationPage = (): React.ReactElement => {
  //Modal state
  const [open, setOpen] = useState<boolean>(false);
  const [alreadyInCohabitationOpen, setAlreadyInCohabitationOpen] = useState<boolean>(false);

  const [queryParams] = useSearchParams();
  // query params
  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, DeclarationCohabitationFormVM>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR,
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
            dataRiferimentoRichiesta:
              filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
          },
        },
      },
      transformer: transformerDeclarationCohabitationData,
    }
  );

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [responseCohabitation, declareCohabitation] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/dichiarazione-convivenza-di-fatto`, {
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedAnprData = responseCohabitation.data as SaveDataAnpr;

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (
      castedAnprData?.errors?.length &&
      castedAnprData?.errors?.length > 0 &&
      visibility.error === undefined
    ) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const navigate = useNavigate();

  const handleSubmit = useCallback(
    (values) => {
      const data = transformerRequestDeclarationCohabitationData(values);

      (
        declareCohabitation({ data }) as ResponsePromise<
          HttpResponse<ResponseAnprSaveData>,
          SaveDataAnpr
        >
      ).then((response) => {
        const castedResponse = response as SaveDataAnpr;

        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }
        toast.success('Operazione eseguita con successo');
        navigate('/mutazioni/persona/convivenza');
      });
    },
    [declareCohabitation, navigate]
  );

  const handleExit = useCallback(() => {
    navigate('/mutazioni/persona/convivenza');
  }, [navigate]);

  const handleOpenModal = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const isLoading = !anpr.data || anpr.isLoading;

  const alreadyInCohabitation = useMemo(() => {
    const dichiarante = (anpr?.data as DeclarationCohabitationFormVM)?.dichiarante;
    return (
      dichiarante?.convivenzaDiFatto &&
      dichiarante?.convivenzaDiFatto?.contrattoRisoluzione === null
    );
  }, [anpr?.data]);

  useEffect(() => {
    if (alreadyInCohabitation) {
      setAlreadyInCohabitationOpen(true);
    }
  }, [alreadyInCohabitation]);

  return !isLoading ? (
    <Fragment>
      <Breadcrumbs items={DeclarationCohabitationCrumbs} />
      <PmiTitle title="Dichiarazione Convivenza" />
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={
          castedAnprData?.warnings?.length === 1
            ? `C'è 1 warning`
            : `Ci sono ${castedAnprData?.warnings?.length} warning`
        }
        messages={castedAnprData?.warnings || []}
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : (castedAnprData?.warnings || [])?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      <Formik
        validationSchema={validationSchema}
        initialValues={anpr.data as DeclarationCohabitationFormVM}
        onSubmit={handleSubmit}
      >
        <Form>
          <Grid container spacing={1}>
            <Grid item xs={12} md={2}>
              <StickyDiv>
                <StepButton active fullWidth>
                  <StepTypography active>Convivenza di fatto</StepTypography>
                </StepButton>
                <div className="pmi-pt-4">
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrandThird"
                      variant="outlined"
                      fullWidth
                      type="submit"
                      id="confirm"
                    >
                      Conferma
                    </PmiButton>
                  </div>
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrand"
                      variant="contained"
                      fullWidth
                      onClick={handleOpenModal}
                      id="deny-operation"
                    >
                      Annulla Operazione
                    </PmiButton>
                  </div>
                </div>
              </StickyDiv>
            </Grid>
            <Grid item xs={12} md={10}>
              <Grid container spacing={1} item xs={12}>
                <Grid item xs={12}>
                  <DeclarationCohabitation />
                </Grid>
                <Grid item xs={12} md={4}>
                  <PmiButton
                    color="cdmBrandThird"
                    variant="outlined"
                    fullWidth
                    type="reset"
                    id="reset"
                  >
                    Ripulisci
                  </PmiButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <ExitModal open={open} onClose={() => setOpen(false)} onConfirm={handleExit} />
        </Form>
      </Formik>
      <ModalAlreadyInCohabitation
        open={alreadyInCohabitationOpen}
        onClose={setAlreadyInCohabitationOpen}
        onConfirm={() => {
          setAlreadyInCohabitationOpen(false);
          navigate('/mutazioni/persona/convivenza');
        }}
      />
    </Fragment>
  ) : (
    <></>
  );
};

DeclarationCohabitationPage.displayName = 'Declaration Cohabitation page component';

export default DeclarationCohabitationPage;
